/**
 * useRKH Hook
 * Custom hook for fetching and managing RKH list data
 */

import { useState, useCallback } from 'react';
import rkhApiService from '../services/rkhApiService';
import { RKH, RKHFilters, RKHPagination } from '../types';

interface UseRKHResult {
    data: RKH[];
    loading: boolean;
    error: string | null;
    pagination: RKHPagination | null;
    fetchData: (filters?: RKHFilters) => Promise<void>;
    setPage: (page: number) => void;
}

export const useRKH = (): UseRKHResult => {
    const [data, setData] = useState<RKH[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<RKHPagination | null>(null);
    const [currentFilters, setCurrentFilters] = useState<RKHFilters | undefined>();

    const fetchData = useCallback(async (filters?: RKHFilters) => {
        setLoading(true);
        setError(null);
        setCurrentFilters(filters);

        try {
            const response = await rkhApiService.getRKHList(filters);
            setData(response.data);
            setPagination(response.pagination);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || 
                               err.response?.data?.detail || 
                               err.message || 
                               'Gagal memuat data RKH';
            setError(errorMessage);
            setData([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const setPage = useCallback((page: number) => {
        if (currentFilters) {
            fetchData({ ...currentFilters, page });
        }
    }, [currentFilters, fetchData]);

    return {
        data,
        loading,
        error,
        pagination,
        fetchData,
        setPage,
    };
};

export default useRKH;
